import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Info } from "lucide-react";

export const PF_FIXED = 1800;

export const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

export const computeSalary = (r) => {
  const b = Number(r.basic) || 0;
  const hraOn = r.hra_applicable !== false;
  const h = hraOn ? (Number(r.hra) || 0) : 0;
  const s = Number(r.special_allowance) || 0;
  const e = Number(r.esi) || 0;
  const t = Number(r.pt) || 0;
  const pfOn = !!r.pf_applicable;
  const pfEmployer = pfOn ? PF_FIXED : 0;
  const pfEmployee = pfOn ? PF_FIXED : 0;
  const gross = b + h + s;
  const ctc = gross + e + pfEmployer;
  const netTakeHome = gross - pfEmployee - t;
  return { ctc, gross, netTakeHome, pfEmployer, pfEmployee };
};

/**
 * Create / Edit dialog for a single salary structure revision.
 * Parent owns form state; this component renders inputs + live CTC preview.
 */
export default function SalaryStructureFormDialog({ open, onOpenChange, form, setForm, onSubmit, busy }) {
  const live = computeSalary(form);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="ss-dialog">
        <DialogHeader>
          <DialogTitle>{form.id ? "Edit" : "Add"} Salary Structure · {form.user_name}</DialogTitle>
          <DialogDescription>
            CTC = Basic + HRA + Special Allowance + Employer PF + ESI. Each effective date creates a new revision.
          </DialogDescription>
        </DialogHeader>

        {/* Policy toggles */}
        <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Label className="text-sm font-semibold">HRA Applicable</Label>
              <p className="text-[11px] text-zinc-500 leading-tight mt-0.5">
                Turn off for staff staying on company accommodation (Housekeeping, Kitchen, Maintenance, Security, Resort Ops).
              </p>
            </div>
            <Switch
              checked={!!form.hra_applicable}
              onCheckedChange={(v) => setForm({ ...form, hra_applicable: v })}
              data-testid="ss-hra-toggle"
            />
          </div>
          <div className="flex items-center justify-between border-t border-zinc-200 pt-3">
            <div className="flex-1">
              <Label className="text-sm font-semibold">PF Applicable</Label>
              <p className="text-[11px] text-zinc-500 leading-tight mt-0.5 flex items-start gap-1">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                Fixes Employee PF = ₹1,800 and Employer PF = ₹1,800 (12% of ₹15,000 wage ceiling).
              </p>
            </div>
            <Switch
              checked={!!form.pf_applicable}
              onCheckedChange={(v) => setForm({ ...form, pf_applicable: v })}
              data-testid="ss-pf-toggle"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Effective From <span className="text-red-500">*</span></Label>
            <Input
              type="date"
              value={form.effective_from}
              onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
              data-testid="ss-effective-from"
            />
          </div>
          <div>
            <Label>Basic Salary <span className="text-red-500">*</span></Label>
            <Input type="number" min="0" step="0.01" value={form.basic}
              onChange={(e) => setForm({ ...form, basic: e.target.value })} data-testid="ss-basic" />
          </div>
          <div>
            <Label className={form.hra_applicable ? "" : "text-zinc-400"}>
              HRA {form.hra_applicable ? "" : "(disabled)"}
            </Label>
            <Input type="number" min="0" step="0.01" value={form.hra}
              disabled={!form.hra_applicable}
              onChange={(e) => setForm({ ...form, hra: e.target.value })} data-testid="ss-hra" />
          </div>
          <div>
            <Label>Special Allowance</Label>
            <Input type="number" min="0" step="0.01" value={form.special_allowance}
              onChange={(e) => setForm({ ...form, special_allowance: e.target.value })} data-testid="ss-special" />
          </div>
          <div>
            <Label>Employer ESI</Label>
            <Input type="number" min="0" step="0.01" value={form.esi}
              onChange={(e) => setForm({ ...form, esi: e.target.value })} data-testid="ss-esi" />
          </div>
          <div>
            <Label>Professional Tax (PT)</Label>
            <Input type="number" min="0" step="0.01" value={form.pt}
              onChange={(e) => setForm({ ...form, pt: e.target.value })} data-testid="ss-pt" />
          </div>
          <div className="col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="bg-zinc-100 rounded-md p-2 text-center" data-testid="ss-pf-employee-display">
              <div className="label-overline">Employee PF</div>
              <div className="text-base font-bold font-mono">
                {form.pf_applicable ? fmtINR(PF_FIXED) : <span className="text-zinc-400 text-sm">Off</span>}
              </div>
            </div>
            <div className="bg-zinc-100 rounded-md p-2 text-center" data-testid="ss-pf-employer-display">
              <div className="label-overline">Employer PF</div>
              <div className="text-base font-bold font-mono">
                {form.pf_applicable ? fmtINR(PF_FIXED) : <span className="text-zinc-400 text-sm">Off</span>}
              </div>
            </div>
          </div>
          <div className="col-span-2">
            <Label>Note (optional)</Label>
            <Textarea rows={2} value={form.note || ""}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="e.g. April 2026 hike, on-property accommodation" />
          </div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3 border-t border-zinc-200 pt-3">
          <div>
            <div className="label-overline">Gross Earnings</div>
            <div className="text-base font-bold font-mono">{fmtINR(live.gross)}</div>
          </div>
          <div>
            <div className="label-overline">Net Take-Home</div>
            <div className="text-base font-bold font-mono text-emerald-700">{fmtINR(live.netTakeHome)}</div>
          </div>
          <div>
            <div className="label-overline">Monthly CTC</div>
            <div className="text-base font-bold font-mono text-zinc-900" data-testid="ss-ctc">{fmtINR(live.ctc)}</div>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={onSubmit}
            disabled={busy || !form.user_id || !form.effective_from}
            className="bg-zinc-950 hover:bg-zinc-800"
            data-testid="ss-save"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
